/**
 * ChatPanel — persistent chat surface for the Pack Studio.
 *
 * Renders the message history, streaming edit results, and the composer.
 * Scrolls to bottom on new messages. Pure UI — fed by `useChat()`.
 */

import { useEffect, useRef } from 'react';
import type { ChatMessage } from './useChat';
import { ChatComposer } from './ChatComposer';
import type { MentionRef } from './ChatComposer';
import type { MentionItem } from './MentionDropdown';
import { EditResultCard } from './EditResultCard';

// ─── Quick Actions ────────────────────────────────────────────────────────────

interface QuickAction {
  label: string;
  prompt: string;
  icon: string;
}

const PACK_QUICK_ACTIONS: QuickAction[] = [
  { label: 'Why this approach?', prompt: 'Explain the creative strategy and why this visual approach was chosen for this persona and scenario.', icon: '💡' },
  { label: 'Punch up the headlines', prompt: 'Make the headlines more punchy and direct. Keep the message but increase impact and clarity.', icon: '✏️' },
  { label: 'Strengthen the CTAs', prompt: 'Improve the call-to-action copy across the ads. Make them more urgent and action-oriented.', icon: '🎯' },
  { label: 'Try a bolder tone', prompt: 'Rework the copy with a bolder, more confident tone of voice that still fits the brand.', icon: '🔥' },
  { label: 'Check persona fit', prompt: 'Review how well these ads align with the target persona\'s motivations, concerns, and buyer scenario.', icon: '👤' },
  { label: 'Suggest variations', prompt: 'Suggest 2-3 creative variations or alternative angles we could try for this pack.', icon: '🎨' },
];

function QuickActionsStrip({
  onSelect,
  disabled,
}: {
  onSelect: (prompt: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="shrink-0 px-3 pt-3 pb-2 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white">
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">
        Suggested Actions
      </p>
      <div className="flex flex-wrap gap-1.5">
        {PACK_QUICK_ACTIONS.map((action) => (
          <button
            key={action.label}
            onClick={() => !disabled && onSelect(action.prompt)}
            disabled={disabled}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-white text-[11px] font-medium text-slate-600 hover:border-brand-teal/50 hover:text-brand-teal hover:bg-teal-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span>{action.icon}</span>
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface ChatPanelProps {
  messages: ChatMessage[];
  activeAdIds: string[];
  isStreaming: boolean;
  /** Edits remaining (share-link credit). Pass `null` to hide the badge entirely (admin / unlimited). */
  editsRemaining?: number | null;
  totalAdCount: number;
  onSend: (content: string, mentions: MentionRef[]) => void;
  onCancel: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  mentionItems?: MentionItem[];
  /** Optional banner shown above the message list when present. */
  errorBanner?: string | null;
}

export function ChatPanel({
  messages,
  activeAdIds,
  isStreaming,
  editsRemaining = null,
  totalAdCount,
  onSend,
  onCancel,
  onSelectAll,
  onClearSelection,
  mentionItems,
  errorBanner,
}: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  // Show quick actions only while no real user/assistant exchange has happened yet
  const hasUserMessages = messages.some((m) => m.role === 'user' || (m.role === 'assistant' && m.content));

  const handleQuickAction = (prompt: string) => {
    onSend(prompt, []);
  };

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-brand-teal animate-pulse" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Studio
            </span>
          </div>
          {editsRemaining !== null && (
            <span
              className={`text-[10px] font-mono border rounded px-2 py-0.5 ${
                editsRemaining === 0
                  ? 'text-danger border-danger/40 bg-red-50'
                  : editsRemaining <= 5
                    ? 'text-warning border-warning/40 bg-amber-50'
                    : 'text-brand-teal-dark border-teal-300 bg-teal-50'
              }`}
            >
              {editsRemaining} edits
            </span>
          )}
        </div>
      </div>

      {errorBanner && (
        <div className="shrink-0 px-3 py-2 text-[11px] text-rose-700 bg-rose-50 border-b border-rose-200">
          {errorBanner}
        </div>
      )}

      {/* Quick action chips — visible until first real exchange */}
      {!hasUserMessages && (
        <QuickActionsStrip onSelect={handleQuickAction} disabled={isStreaming} />
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {isStreaming && (
          <div className="flex items-center gap-2 px-1 py-1">
            <div className="flex gap-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-[10px] font-mono text-slate-400">Editing...</span>
          </div>
        )}
      </div>

      {/* Composer */}
      <ChatComposer
        activeAdCount={activeAdIds.length}
        isStreaming={isStreaming}
        onSend={onSend}
        onCancel={onCancel}
        onSelectAll={onSelectAll}
        onClearSelection={onClearSelection}
        totalAdCount={totalAdCount}
        mentionItems={mentionItems}
      />
    </div>
  );
}

const MENTION_PILL_STYLES: Record<string, string> = {
  attachment: 'bg-teal-50 text-teal-700 border-teal-200',
  ad: 'bg-blue-50 text-blue-700 border-blue-200',
  persona: 'bg-purple-50 text-purple-700 border-purple-200',
};
const DEFAULT_PILL = 'bg-slate-100 text-slate-600 border-slate-200';

function RichContent({ text, mentions }: { text: string; mentions?: ChatMessage['mentions'] }) {
  const mentionMap = new Map((mentions ?? []).map((m) => [m.label, m.type]));
  const parts = text.split(/(@\S+)/g);

  return (
    <span className="whitespace-pre-wrap">
      {parts.map((part, i) => {
        if (part.startsWith('@') && part.length > 1) {
          const label = part.slice(1);
          const type = mentionMap.get(label);
          const cls = (type && MENTION_PILL_STYLES[type]) || DEFAULT_PILL;
          return (
            <span
              key={i}
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${cls}`}
            >
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  if (message.role === 'system') {
    return (
      <div className="px-3 py-2 text-[11px] text-slate-500 font-mono leading-relaxed bg-white rounded-lg border border-slate-100">
        {message.content}
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] space-y-1">
          <div className="bg-teal-50 border border-teal-200 rounded-lg rounded-br-sm px-3 py-2">
            <p className="text-sm text-slate-800">
              <RichContent text={message.content} mentions={message.mentions} />
            </p>
          </div>
          {message.target_ads.length > 0 && (
            <p className="text-[9px] font-mono text-slate-400 text-right">
              → {message.target_ads.length} ad{message.target_ads.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="max-w-[95%] space-y-2">
      {message.content && (
        <div className="bg-white border border-slate-200 rounded-lg rounded-bl-sm px-3 py-2 shadow-sm">
          <p className="text-sm text-slate-700">
            <RichContent text={message.content} mentions={message.mentions} />
          </p>
        </div>
      )}
      {message.edit_results.length > 0 && (
        <div className="space-y-2 pl-1">
          {message.edit_results.map((result) => (
            <EditResultCard key={result.edit_id} result={result} />
          ))}
        </div>
      )}
    </div>
  );
}
